import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Bell, Check, CheckCheck, ChevronUp, Trash2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import useScrollLoadMore from "../common/useScrollLoadMore";
import "../Trainer/TrainerDashboard.css";
import {
  deleteNotification,
  getNotificationsPage,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../../services/NotificationService";
import { useNotificationRecipient } from "../../../utils/useNotificationRecipient";
import {
  getNotificationFrom,
  getNotificationSource,
  getPriorityLabel,
  summarizeNotification,
  ATTENDANCE_STATUS,
  getAttendanceStatus,
} from "../../../utils/notificationSource";
import { subscribeToNotifications } from "../NotificationBell/notificationPulse";
import { dedupeNotifications } from "../../../utils/notificationDedupe";

/* =============================================================
   NOTIFICATIONS

   Laid out the way a notification centre is, not the way a report
   is: a feed down one column, sectioned by day, with each source's
   notifications gathered into a stack that opens on a press. It was a
   five-column data table -- which answered "sort these by priority",
   a question nobody brings to their notifications, and not "what
   happened, and what is new", which everyone does.

   The styling is the house's, not Apple's: flat cards on one hairline,
   no shadows, the ink ladder for text, the palette for decoration and
   literal status tints for priority. See the td-ntf- block at the end
   of TrainerDashboard.css.
   ============================================================= */

const PAGE_SIZE = 20;
const DAY_MS = 86400000;
// A collapsed stack shows at most two plates behind its top card.
const MAX_LAYERS = 3;

const PRIORITY_PILL = {
  High: "pg-pill--bad",
  Medium: "pg-pill--warn",
  Low: "pg-pill--good",
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const dayKeyOf = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 0 : startOfDay(d);
};

const dayTitle = (key) => {
  if (!key) return "Earlier";
  const days = Math.round((startOfDay(new Date()) - key) / DAY_MS);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  const d = new Date(key);
  if (days < 7) return d.toLocaleDateString("en-IN", { weekday: "long" });
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

/* Relative while it is recent, the clock after that -- the section
   heading already says which day. */
const timeLabel = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 6 * 60 && startOfDay(d) === startOfDay(new Date())) {
    return `${Math.floor(mins / 60)}h ago`;
  }
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
};

const formatFullDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// `topics` is stored comma-separated (the attendance record's own
// convention); split it back into the list the trainer actually picked.
const parseTopics = (topics) =>
  (Array.isArray(topics) ? topics : String(topics || "").split(","))
    .map((t) => String(t).trim())
    .filter(Boolean);

const groupIdOf = (n) => `${dayKeyOf(n.createdAt)}:${getNotificationSource(n).key}`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ── One notification ─────────────────────────────────────────────
   Two targets side by side, never one inside the other: the card
   opens the notification, and Mark read / Delete sit in their own
   column beside it. Delete is only drawn when the page passes an
   `onDelete` -- students do not get one. `moreCount` is set only on
   the top card of a collapsed stack. */
const NotificationCard = ({ n, isFocus, moreCount = 0, onOpen, onMarkRead, onDelete, onExpand }) => {
  const source = getNotificationSource(n);
  const Icon = source.icon;
  const priority = getPriorityLabel(n.priority);
  const summary = summarizeNotification(n);
  const from = getNotificationFrom(n);
  // Attendance only: present (green) or absent (red), as the API recorded it.
  const status = getAttendanceStatus(n);

  const spoken = [
    n.read ? null : "Unread",
    n.title || "Notification",
    status ? `Marked ${status}` : null,
    summary || null,
    `From ${from}`,
    priority ? `${priority} priority` : null,
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <article
      id={`notification-${n.id}`}
      className={`td-ntf-card${n.read ? "" : " is-unread"}${isFocus ? " is-focus" : ""}${
        status ? ` td-ntf-card--${status}` : ""
      }`}
    >
      <div className="td-ntf-card-row">
        <button type="button" className="td-ntf-card-hit" onClick={() => onOpen(n)} aria-label={spoken}>
          <span className="td-ntf-card-icon" aria-hidden="true">
            <Icon />
          </span>

          <span className="td-ntf-card-main">
            <span className="td-ntf-card-head">
              <span className="td-ntf-card-src">{source.label}</span>
              {!n.read && <span className="td-ntf-dot" aria-hidden="true" />}
              <time className="td-ntf-card-time" dateTime={n.createdAt}>
                {timeLabel(n.createdAt)}
              </time>
            </span>

            <span className="td-ntf-card-title">{n.title || "Notification"}</span>
            {summary && <span className="td-ntf-card-msg">{summary}</span>}

            <span className="td-ntf-card-meta">
              <span className="td-ntf-card-from">
                From <b>{from}</b>
              </span>
              {status && (
                <span className={`pgm-pill ${ATTENDANCE_STATUS[status].pill}`}>
                  {ATTENDANCE_STATUS[status].label}
                </span>
              )}
              {priority && (
                <span className={`pg-pill ${PRIORITY_PILL[priority] || ""}`}>{priority}</span>
              )}
            </span>
          </span>
        </button>

        <div className="td-ntf-card-acts">
          {!n.read && (
            <button
              type="button"
              className="td-ntf-act"
              onClick={() => onMarkRead(n.id)}
              aria-label={`Mark "${n.title || "notification"}" as read`}
              title="Mark as read"
            >
              <Check aria-hidden="true" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="td-ntf-act td-ntf-act--danger"
              onClick={() => onDelete(n.id)}
              aria-label={`Delete "${n.title || "notification"}"`}
              title="Delete"
            >
              <Trash2 aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {moreCount > 0 && (
        <button type="button" className="td-ntf-more" onClick={onExpand} aria-expanded="false">
          {moreCount} more from {source.label}
        </button>
      )}
    </article>
  );
};

const Notifications = () => {
  const { email, recipientType, isStudent } = useNotificationRecipient();
  const location = useLocation();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // A scroll batch, as against a fresh load. Separate because a fresh
  // load with nothing on screen shows the loading state, and an appended
  // page must not replace the feed with one.
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openNotif, setOpenNotif] = useState(null);
  // Stacks the reader has opened, by day + source.
  const [expanded, setExpanded] = useState(() => new Set());
  // Re-renders the relative times ("4m ago") once a minute.
  const [, setClock] = useState(0);

  // The foot under the feed -- the sentinel, in the flow of the page.
  const listEndRef = useRef(null);
  // The navigation whose notification has already been scrolled to, so a
  // live refresh does not yank the page back to it.
  const scrolledForRef = useRef(null);
  // The navigation whose named notification has already been brought out
  // from behind the filter -- see the effect that reads it.
  const widenedForRef = useRef(null);

  // The bell and its toasts link here naming one notification.
  const focusId = location.state?.notificationId || null;

  const load = useCallback(async () => {
    if (!email || !recipientType) return;
    // Page 1 replaces what is on screen; anything past it came from the
    // scroll and adds to it.
    const isAppend = page > 1;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const result = await getNotificationsPage(email, recipientType, page, PAGE_SIZE);
      setNotifications((prev) => {
        if (!isAppend) return dedupeNotifications(result.notifications);
        // The same row can arrive twice if one is added server-side between
        // two batches and pushes the page boundary along.
        const seen = new Set(prev.map((n) => n.id ?? n._id));
        return dedupeNotifications([...prev, ...result.notifications.filter((n) => !seen.has(n.id ?? n._id))]);
      });
      setUnreadCount(result.unreadCount);
      setPagination(result.pagination);
    } catch (e) {
      setError("We could not load your notifications. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [email, recipientType, page]);

  useEffect(() => {
    load();
  }, [load]);

  /* Live. The bell publishes a pulse the moment it sees something arrive;
     the feed re-reads its first page on that beat rather than waiting for
     a reload. Held in refs so the subscription is made once and always
     calls the current page and loader. Below `load`, which it reads. */
  const pageRef = useRef(page);
  pageRef.current = page;
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(
    () =>
      subscribeToNotifications((pulse) => {
        // A withdrawn notification closes if it is the one being read.
        if (pulse.type === "removed") {
          const gone = new Set((pulse.ids || []).map(String));
          setOpenNotif((current) => (current && gone.has(String(current.id)) ? null : current));
        }
        if (pageRef.current === 1) loadRef.current();
        else setPage(1);
      }),
    [],
  );

  useEffect(() => {
    const t = setInterval(() => setClock((c) => c + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const handleMarkRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await markNotificationRead(id);
    } catch (e) {
      load();
    }
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead(email, recipientType);
    } catch (e) {
      load();
    }
  };

  const handleDelete = async (id) => {
    const removed = notifications.find((n) => n.id === id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (removed && !removed.read) setUnreadCount((c) => Math.max(0, c - 1));
    setOpenNotif((current) => (current && current.id === id ? null : current));
    try {
      await deleteNotification(id);
    } catch (e) {
      load();
    }
  };

  // Opening a notification is what marks it read -- the same gesture.
  const openNotification = (row) => {
    setOpenNotif(row);
    if (!row.read) handleMarkRead(row.id);
  };

  const toggleGroup = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* The sources present in what has loaded, in the order they first
     appear -- a filter for a source with nothing in it is a dead end. */
  const sourceFilters = useMemo(() => {
    const seen = new Map();
    notifications.forEach((n) => {
      const s = getNotificationSource(n);
      if (!seen.has(s.key)) seen.set(s.key, { key: `src:${s.key}`, label: s.label });
    });
    return [...seen.values()];
  }, [notifications]);

  const visible = useMemo(() => {
    if (filter === "all") return notifications;
    if (filter === "unread") return notifications.filter((n) => !n.read);
    const key = filter.slice(4);
    return notifications.filter((n) => getNotificationSource(n).key === key);
  }, [notifications, filter]);

  /* Day -> source -> notifications. The server returns newest first, and
     Maps keep insertion order, so sections, stacks and the cards inside a
     stack all come out newest first without sorting anything again. */
  const days = useMemo(() => {
    const byDay = new Map();
    visible.forEach((n) => {
      const dayKey = dayKeyOf(n.createdAt);
      if (!byDay.has(dayKey)) byDay.set(dayKey, new Map());
      const groups = byDay.get(dayKey);
      const source = getNotificationSource(n);
      if (!groups.has(source.key)) {
        groups.set(source.key, { id: `${dayKey}:${source.key}`, source, items: [] });
      }
      groups.get(source.key).items.push(n);
    });
    return [...byDay.entries()].map(([key, groups]) => {
      const list = [...groups.values()];
      return {
        key,
        title: dayTitle(key),
        count: list.reduce((sum, g) => sum + g.items.length, 0),
        groups: list,
      };
    });
  }, [visible]);

  /* CATEGORY -> day -> notifications, which is what the All tab shows.

     All used to be one feed down the page, sectioned by day: attendance
     under Monday, then a batch change, then a message, then attendance
     again on Tuesday. Every source the reader has is in there, and the
     only way to read one of them was to leave All and pick its chip.
     Sections are by source now, in the order each last had something to
     say, and the days sit inside them -- so "what happened with my
     batches" is answerable without changing tab, and the day a card
     belongs to is still stated, which the card's own clock does not say
     once it is older than today.

     The other tabs keep the day-first feed: a source chip is already one
     category, and Unread is a queue rather than a subject. */
  const categories = useMemo(() => {
    const bySource = new Map();
    visible.forEach((n) => {
      const source = getNotificationSource(n);
      if (!bySource.has(source.key)) {
        bySource.set(source.key, { key: source.key, source, count: 0, days: new Map() });
      }
      const entry = bySource.get(source.key);
      entry.count += 1;
      const dayKey = dayKeyOf(n.createdAt);
      if (!entry.days.has(dayKey)) {
        entry.days.set(dayKey, { key: dayKey, title: dayTitle(dayKey), items: [] });
      }
      entry.days.get(dayKey).items.push(n);
    });
    // Maps keep insertion order and the server sends newest first, so the
    // category that spoke most recently leads, and so does its newest day.
    return [...bySource.values()].map((c) => ({ ...c, days: [...c.days.values()] }));
  }, [visible]);

  /* Arriving from a toast or the bell: make sure the named notification
     is on screen -- widen a filter that hides it, open its stack -- and
     then bring it into view once for that navigation.

     Once for that navigation, and not once per render of it: this ran on
     every change of `filter` and of the feed, so as long as the reader had
     come in from the bell, pressing Attendance, Batches or Events put the
     filter straight back to All and every category read as though it held
     everything -- attendance notifications under Batches and Events most
     of all, those being what a student is sent most. A live refresh threw
     the filter away the same way. The ref closes both. */
  useEffect(() => {
    if (!focusId) return;
    // location.key changes on every navigation, so a second visit naming
    // the same notification is honoured again.
    const navKey = `${location.key}:${focusId}`;
    if (widenedForRef.current === navKey) return;
    const target = notifications.find((n) => String(n.id) === String(focusId));
    // Not loaded yet -- leave the navigation unmarked so the page it
    // arrives on gets this treatment.
    if (!target) return;
    widenedForRef.current = navKey;
    const hiddenByFilter =
      (filter === "unread" && target.read) ||
      (filter.startsWith("src:") && filter.slice(4) !== getNotificationSource(target).key);
    if (hiddenByFilter) setFilter("all");
    const gid = groupIdOf(target);
    setExpanded((prev) => (prev.has(gid) ? prev : new Set(prev).add(gid)));
  }, [focusId, notifications, filter, location.key]);

  useEffect(() => {
    if (!focusId || scrolledForRef.current === location.key) return;
    const el = document.getElementById(`notification-${focusId}`);
    if (!el) return;
    scrolledForRef.current = location.key;
    el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" });
  }, [focusId, location.key, days, expanded]);

  // Escape closes the popup, as it does every sheet in the app.
  useEffect(() => {
    if (!openNotif) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenNotif(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openNotif]);

  const totalPages = pagination?.totalPages || 1;
  const totalRecords = pagination?.total ?? notifications.length;

  /* Infinite scroll: the page is the scroller, and the foot of the feed
     reaching the viewport asks for the next page. Below `visible` and
     `totalPages`, which it reads. */
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loading || loadingMore) return;
      if (page >= totalPages) return;
      setPage((p) => p + 1);
    },
    [visible.length, page, totalPages, loading, loadingMore],
  );

  if (!email || !recipientType) {
    return (
      <div className="pg">
        <div className="pg-state">
          <span className="pg-state-icon">
            <Bell size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">Notifications are not available for this account</h3>
          <p className="pg-state-text">
            Sign in as a student or trainer to see your notification history.
          </p>
        </div>
      </div>
    );
  }

  // A source filter is already one stream, so its stacks stay open.
  const forceOpen = filter.startsWith("src:");
  // A student reads their notifications and marks them read; clearing
  // them out is not theirs to do, so neither the card nor the open
  // notification offers Delete.
  const canDelete = !isStudent;
  const cardProps = {
    onOpen: openNotification,
    onMarkRead: handleMarkRead,
    onDelete: canDelete ? handleDelete : null,
  };

  const filters = [
    { key: "all", label: "All", count: totalRecords },
    { key: "unread", label: "Unread", count: unreadCount },
    ...sourceFilters,
  ];

  let feed;
  if (error) {
    feed = (
      <div className="pg-state">
        <span className="pg-state-icon">
          <AlertCircle size={26} aria-hidden="true" />
        </span>
        <h3 className="pg-state-title">Unable to load notifications</h3>
        <p className="pg-state-text">{error}</p>
        <button type="button" className="pg-btn" onClick={load}>
          Try again
        </button>
      </div>
    );
  } else if (loading && notifications.length === 0) {
    feed = (
      <div className="pg-state">
        <span className="pg-spinner" role="status" aria-hidden="true" />
        <p className="pg-state-text">Loading your notifications...</p>
      </div>
    );
  } else if (notifications.length === 0) {
    feed = (
      <div className="pg-state">
        <span className="pg-state-icon">
          <Bell size={26} aria-hidden="true" />
        </span>
        <h3 className="pg-state-title">No notifications yet</h3>
        <p className="pg-state-text">
          Updates about your batches, classes and messages will appear here.
        </p>
      </div>
    );
  } else if (visible.length === 0) {
    feed = (
      <div className="pg-state">
        <span className="pg-state-icon">
          <CheckCheck size={26} aria-hidden="true" />
        </span>
        <h3 className="pg-state-title">
          {filter === "unread" ? "You're all caught up" : "Nothing to show here"}
        </h3>
        <p className="pg-state-text">
          {filter === "unread"
            ? "Every notification has been read."
            : "No loaded notification matches this filter."}
        </p>
        <button type="button" className="pg-btn" onClick={() => setFilter("all")}>
          Show all
        </button>
      </div>
    );
  } else if (filter === "all") {
    /* Segregated by category: a section per source, its days inside it.
       Same two headings the day feed uses -- the group head names the
       category, the day head names the day -- so nothing new is drawn,
       it is drawn the other way up. */
    feed = (
      <>
        {categories.map((category) => {
          const CategoryIcon = category.source.icon;
          return (
            <section
              className="td-ntf-day"
              key={category.key}
              aria-labelledby={`td-ntf-cat-${category.key}`}
            >
              <div className="td-ntf-group-head">
                <span className="td-ntf-group-title" id={`td-ntf-cat-${category.key}`}>
                  <CategoryIcon aria-hidden="true" />
                  {category.source.label}
                  <span className="td-ntf-day-n">{category.count}</span>
                </span>
              </div>

              {category.days.map((day) => (
                <section className="td-ntf-day" key={`${category.key}:${day.key}`}>
                  <h3 className="td-ntf-day-head">
                    <span className="td-ntf-day-title">{day.title}</span>
                    <span className="td-ntf-day-n">{day.items.length}</span>
                  </h3>

                  <ul className="td-ntf-list">
                    {day.items.map((n) => (
                      <li key={n.id}>
                        <NotificationCard
                          n={n}
                          isFocus={String(n.id) === String(focusId)}
                          {...cardProps}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </section>
          );
        })}

        <div className="pg-scroll-foot" ref={listEndRef}>
          {notifications.length < totalRecords ? (
            <>
              <span className="pg-scroll-count">
                {notifications.length} of {totalRecords}
              </span>
              {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
            </>
          ) : (
            <span className="pg-scroll-count">
              All {totalRecords} notification{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )}
        </div>
      </>
    );
  } else {
    feed = (
      <>
        {days.map((day) => (
          <section className="td-ntf-day" key={day.key} aria-labelledby={`td-ntf-day-${day.key}`}>
            <h2 className="td-ntf-day-head" id={`td-ntf-day-${day.key}`}>
              <span className="td-ntf-day-title">{day.title}</span>
              <span className="td-ntf-day-n">{day.count}</span>
            </h2>

            <ul className="td-ntf-list">
              {day.groups.map((group) => {
                const isOpen = forceOpen || group.items.length === 1 || expanded.has(group.id);

                if (!isOpen) {
                  const layers = Math.min(group.items.length, MAX_LAYERS);
                  const top = group.items[0];
                  return (
                    <li key={group.id} className={`td-ntf-stack td-ntf-stack--${layers}`}>
                      <NotificationCard
                        n={top}
                        isFocus={String(top.id) === String(focusId)}
                        moreCount={group.items.length - 1}
                        onExpand={() => toggleGroup(group.id)}
                        {...cardProps}
                      />
                    </li>
                  );
                }

                if (group.items.length === 1) {
                  const only = group.items[0];
                  return (
                    <li key={group.id}>
                      <NotificationCard
                        n={only}
                        isFocus={String(only.id) === String(focusId)}
                        {...cardProps}
                      />
                    </li>
                  );
                }

                const GroupIcon = group.source.icon;
                return (
                  <li key={group.id} className="td-ntf-group">
                    <div className="td-ntf-group-head">
                      <span className="td-ntf-group-title">
                        <GroupIcon aria-hidden="true" />
                        {group.source.label}
                        <span className="td-ntf-day-n">{group.items.length}</span>
                      </span>
                      {!forceOpen && (
                        <button
                          type="button"
                          className="td-ntf-link"
                          onClick={() => toggleGroup(group.id)}
                          aria-expanded="true"
                        >
                          <ChevronUp aria-hidden="true" />
                          Show less
                        </button>
                      )}
                    </div>
                    <ul className="td-ntf-list">
                      {group.items.map((n) => (
                        <li key={n.id}>
                          <NotificationCard
                            n={n}
                            isFocus={String(n.id) === String(focusId)}
                            {...cardProps}
                          />
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        <div className="pg-scroll-foot" ref={listEndRef}>
          {notifications.length < totalRecords ? (
            <>
              <span className="pg-scroll-count">
                {notifications.length} of {totalRecords}
              </span>
              {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
            </>
          ) : (
            <span className="pg-scroll-count">
              All {totalRecords} notification{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )}
        </div>
      </>
    );
  }

  /* ── The record popup ──────────────────────────────────────────── */
  const detail = openNotif
    ? (() => {
        const n = openNotif;
        const source = getNotificationSource(n);
        const priority = getPriorityLabel(n.priority);
        const topics = parseTopics(n.topics);
        const status = getAttendanceStatus(n);

        return (
          <div
            className="pg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="notif-detail-title"
            onClick={() => setOpenNotif(null)}
          >
            <div
              className="pg-modal-panel pg-modal-panel--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="pgm-head">
                <div className="pgm-head-main">
                  <span className="pg-modal-eyebrow">{source.label}</span>
                  <h2 className="pgm-batch" id="notif-detail-title">
                    {n.title || "Notification"}
                  </h2>
                  <dl className="pgm-ident">
                    <div className="pgm-ident-row">
                      <dt>From</dt>
                      <dd>{getNotificationFrom(n)}</dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Batch</dt>
                      <dd>{n.batchName || "—"}</dd>
                    </div>
                    {status && (
                      <div className="pgm-ident-row">
                        <dt>Attendance</dt>
                        <dd>
                          <span className={`pgm-pill ${ATTENDANCE_STATUS[status].pill}`}>
                            {status === "present" ? "Marked present" : "Marked absent"}
                          </span>
                        </dd>
                      </div>
                    )}
                    <div className="pgm-ident-row">
                      <dt>Priority</dt>
                      <dd>
                        {priority ? (
                          <span className={`pg-pill ${PRIORITY_PILL[priority] || ""}`}>
                            {priority}
                          </span>
                        ) : (
                          "—"
                        )}
                      </dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Received</dt>
                      <dd>{formatFullDate(n.createdAt)}</dd>
                    </div>
                  </dl>
                </div>

                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={() => setOpenNotif(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              <div className="pg-modal-body">
                <section className="pgm-section">
                  <h3 className="pgm-section-title">Message</h3>
                  {n.message ? (
                    <p className="pgm-sub">{n.message}</p>
                  ) : (
                    <p className="pgm-empty">This notification carries no message.</p>
                  )}
                </section>

                {/* A feedback reply keeps the reply itself apart from the
                    sentence that introduces it, so it can read as a quote. */}
                {n.replyText && (
                  <section className="pgm-section">
                    <h3 className="pgm-section-title">Reply</h3>
                    <blockquote className="td-ntf-quote">{n.replyText}</blockquote>
                  </section>
                )}

                {/* Only attendance notifications carry topics; an empty
                    "Topics covered" on every other kind said nothing. */}
                {topics.length > 0 && (
                  <section className="pgm-section">
                    <h3 className="pgm-section-title">
                      Topics covered
                      <span className="pgm-section-count">{topics.length}</span>
                    </h3>
                    <table className="pgm-topics-table">
                      <thead>
                        <tr>
                          <th className="pgm-num">#</th>
                          <th>Topic</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topics.map((topic, i) => (
                          <tr key={`${topic}-${i}`}>
                            <td className="pgm-num">{i + 1}</td>
                            <td>{topic}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>
                )}
              </div>

              {/* Close stays last and is the only filled button, so the
                  destructive one is never the default under the cursor. */}
              <footer className="pg-modal-foot">
                {canDelete && (
                  <div className="pgm-actions">
                    <button
                      type="button"
                      className="pgm-btn pgm-btn--danger"
                      onClick={() => handleDelete(n.id)}
                    >
                      Delete
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setOpenNotif(null)}
                >
                  Close
                </button>
              </footer>
            </div>
          </div>
        );
      })()
    : null;

  return (
    <div className="pg">
      <div className="td-ntf">
        <header className="pg-head">
          <div className="pg-head-main">
            <h1 className="pg-title">Notifications</h1>
            <p className="pg-sub">
              {unreadCount > 0
                ? `${unreadCount} unread`
                : notifications.length > 0
                  ? "You're all caught up"
                  : "Updates about your batches, classes and messages"}
            </p>
          </div>

          <div className="pg-head-actions">
            <button
              type="button"
              className="pg-btn"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
            >
              <CheckCheck size={15} aria-hidden="true" />
              Mark all read
            </button>
          </div>
        </header>

        {notifications.length > 0 && (
          <div className="td-ntf-seg" role="group" aria-label="Show notifications">
            {filters.map((f) => (
              <button
                type="button"
                key={f.key}
                className={`td-ntf-seg-btn${filter === f.key ? " is-on" : ""}`}
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                {f.count !== undefined && <span className="td-ntf-seg-n">{f.count}</span>}
              </button>
            ))}
          </div>
        )}

        <div className="td-ntf-feed">{feed}</div>
      </div>

      {detail}
    </div>
  );
};

export default Notifications;
